import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';

const GymSettings = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, loading: authLoading } = useAuth();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || !isGymOwner)) {
      navigate('/login');
    }
  }, [user, isGymOwner, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id) {
      loadCurrentLogo();
    }
  }, [currentTenant]);

  const loadCurrentLogo = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('tenants')
        ?.select('logo_url')
        ?.eq('id', currentTenant?.id)
        ?.single();

      if (fetchError) throw fetchError;
      setCurrentLogo(data?.logo_url);
    } catch (err) {
      console.error('Error loading logo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!validTypes?.includes(file?.type)) {
      setError('Please upload a valid image file (JPEG, PNG, WebP, or SVG)');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5242880) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!logoFile) {
      setError('Please select a logo file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Delete old logo if exists
      if (currentLogo) {
        const oldPath = currentLogo?.split('/gym-logos/')?.[1];
        if (oldPath) {
          await supabase?.storage?.from('gym-logos')?.remove([oldPath]);
        }
      }

      // Upload new logo
      const fileExt = logoFile?.name?.split('.')?.pop();
      const fileName = `${currentTenant?.id}/logo-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase?.storage
        ?.from('gym-logos')
        ?.upload(fileName, logoFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('gym-logos')
        ?.getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl;

      // Update tenant logo_url
      const { error: updateError } = await supabase
        ?.from('tenants')
        ?.update({ logo_url: publicUrl })
        ?.eq('id', currentTenant?.id);

      if (updateError) throw updateError;

      setCurrentLogo(publicUrl);
      setLogoFile(null);
      setLogoPreview(null);
      setSuccess('Logo updated successfully! Refresh the page to see changes.');
      
      // Reload after 2 seconds
      setTimeout(() => {
        window.location?.reload();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin-dashboard')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Gym Settings</h1>
          <p className="text-muted-foreground">Customize your gym's branding and appearance</p>
        </div>

        {/* Logo Customization Section */}
        <div className="bg-card rounded-lg border border-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Image" className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Gym Logo</h2>
              <p className="text-sm text-muted-foreground">Upload your gym's logo to personalize the experience</p>
            </div>
          </div>

          {/* Current Logo */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">Current Logo</label>
            <div className="flex items-center justify-center w-full h-48 bg-muted rounded-lg border-2 border-dashed border-border">
              {currentLogo ? (
                <AppImage
                  src={currentLogo}
                  alt="Current gym logo"
                  className="max-h-40 max-w-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <Icon name="ImageOff" className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No logo uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Requirements */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Icon name="Info" className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Logo Requirements</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• <strong>File formats:</strong> JPEG, PNG, WebP, or SVG</li>
                  <li>• <strong>Maximum size:</strong> 5MB</li>
                  <li>• <strong>Recommended dimensions:</strong> 512x512 pixels (square) or 1024x256 pixels (horizontal)</li>
                  <li>• <strong>Background:</strong> Transparent PNG recommended for best results</li>
                  <li>• <strong>Usage:</strong> Logo will appear in the header across all pages</li>
                </ul>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">Upload New Logo</label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                onChange={handleFileSelect}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors"
              >
                <Icon name="Upload" size={18} />
                <span>Choose File</span>
              </label>
              {logoFile && (
                <span className="text-sm text-foreground">{logoFile?.name}</span>
              )}
            </div>
          </div>

          {/* Preview */}
          {logoPreview && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-3">Preview</label>
              <div className="flex items-center justify-center w-full h-48 bg-muted rounded-lg border-2 border-border">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="max-h-40 max-w-full object-contain"
                />
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleUpload}
              disabled={!logoFile || uploading}
              iconName={uploading ? 'Loader2' : 'Save'}
              iconPosition="left"
              iconClassName={uploading ? 'animate-spin' : ''}
            >
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </Button>
            {logoFile && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default GymSettings;